import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { SIZE_LABELS, sizeFromHours, workingDayOf } from '@rundown/domain';
import { Button, Modal, Pill } from '../../components';
import { formatDate, formatHours } from '../../lib/format';
import { cx } from '../../lib/cx';
import { useCapability } from '../workspace/useCapability';
import { useSizeThresholds } from '../workspace/useWorkspace';
import { TYPE_LABELS, TYPE_TONES } from '../labels';
import { LOAD_TEXT, Meter, SectionLabel, StatePill, StatusText } from '../sprints/parts';
import { TIMELINE_STATE_LABEL, likelyToCarryOver, sprintDate, sprintRange } from '../sprints/sprintMeta';
import { ChangeOfPlan } from '../sprints/ChangeOfPlan';
import { carryOverItems, invalidatePlanning, releaseSprint, unstartSprint } from '../sprints/api';
import { SprintAdmin } from '../sprints/SprintAdmin';
import { useHolidays } from '../calendar/useHolidays';
import type { BacklogRow } from '../backlog/api';
import type { DeliveryHotfix } from '../sprints/api';
import type { Mark, PlanCardModel, PlanColumn } from './usePlanBoard';

/**
 * A sprint in full, beside the columns rather than on a screen of its own.
 *
 * Everything here used to BE the Sprints screen. It is the half of that screen
 * the columns could not carry — frozen scope with the reason each item is in
 * it, the hotfix lane eating the reserve, the change-of-plan history, and
 * releasing the week — and it turns out none of it needed its own destination,
 * only somewhere to appear when you ask about one sprint.
 */

/** Help text per state, since what you may do about a sprint depends on it. */
const HELP: Readonly<Record<PlanColumn['state'], string>> = {
  started:
    'Running now. Scope is fixed: new requests go to the backlog, urgent ones to the hotfix track. Changing what is in this sprint takes an agreed change of plan.',
  locked: 'Scope is fixed and the client has been told what is coming, but the week has not begun.',
  planning:
    'Not locked yet. Committed work is what has been placed; the rest is what the ranking would pull in. Locking fixes the scope; starting begins the week.',
  projected:
    'A projection, not a commitment. It changes whenever the backlog is re-ranked or an earlier sprint runs over.',
  released: 'What shipped in this sprint. Unfinished work carried over to the backlog when it closed.',
};

export function SprintDrawer({
  workspaceId,
  column,
  today,
  backlog,
  onClose,
}: {
  readonly workspaceId: string;
  readonly column: PlanColumn;
  readonly today: string;
  readonly backlog: readonly BacklogRow[];
  readonly onClose: () => void;
}) {
  const canPlan = useCapability('planning.write');
  const canAdmin = useCapability('workspace.admin');
  const holidays = useHolidays(workspaceId);
  const { sprint, state } = column;
  const frozen = column.frozen && state !== 'released';
  const day =
    state === 'started' ? workingDayOf(sprint.startsOn, sprint.endsOn, today, holidays.dates) : null;
  const atRisk = frozen && sprint.hotfixHours > sprint.reserveHours;

  return (
    <section className="animate-enter mt-8 border-t border-line pt-6">
      <div className="mb-1.5 flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h2 className="fs-21 font-bold tracking-[-0.015em] whitespace-nowrap text-ink">{sprint.name}</h2>
        <StatePill state={state}>{TIMELINE_STATE_LABEL[state]}</StatePill>
        <span className="fs-12 whitespace-nowrap text-muted">
          {sprintRange(sprint.startsOn, sprint.endsOn, true)}
          {day !== null && ` · day ${day.day} of ${day.of}`}
        </span>
        <span
          className={cx(
            'ml-auto fs-12 font-semibold whitespace-nowrap',
            column.band === null ? 'text-success' : LOAD_TEXT[column.band],
          )}
        >
          {column.band === null
            ? `${formatHours(column.hours)} delivered`
            : `${column.loadLabel} · ${formatHours(column.hours)} of ${formatHours(sprint.plannableHours)}`}
        </span>
        {frozen && canPlan && <ReleaseButton workspaceId={workspaceId} entry={column} />}
        {state === 'started' && canPlan && <UnstartButton workspaceId={workspaceId} entry={column} />}
        {canAdmin && state !== 'released' && (
          <SprintAdmin workspaceId={workspaceId} sprint={sprint} state={sprint.state} />
        )}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close sprint detail"
          className="rounded-control p-1 text-muted hover:text-ink"
        >
          <X aria-hidden size={16} />
        </button>
      </div>
      <p className="mb-6 max-w-[41rem] fs-13 text-muted">{HELP[state]}</p>

      {atRisk && <RiskBanner entry={column} />}

      {frozen ? (
        <div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
          <div className="flex min-w-0 flex-col gap-7">
            <ItemList
              label={`Sprint items · ${column.items.filter((item) => item.removedAt === null).length}`}
              aside={`≈ ${formatHours(column.hours)} planned`}
              items={column.items}
              empty="Nothing was committed to this sprint."
            />
            <ChangeOfPlan workspaceId={workspaceId} timeline={column} backlog={backlog} />
          </div>
          <HotfixLane entry={column} />
        </div>
      ) : (
        <div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
          <div className="min-w-0">
            <ItemList
              label={
                state === 'released'
                  ? `Delivered · ${column.items.filter((item) => item.mark === 'delivered').length}`
                  : `${state === 'planning' ? 'Proposed' : 'Projected'} · ${column.items.length}`
              }
              aside={`≈ ${formatHours(column.hours)}`}
              items={column.items}
              empty="Nothing here yet. The backlog ranking fills this sprint as capacity allows, or drag work into its column above."
            />
          </div>
          {state === 'released' ? <HotfixLane entry={column} past /> : <CapacityAside entry={column} />}
        </div>
      )}
    </section>
  );
}

/** Taking a start back, when the week turns out not to have begun after all. */
function UnstartButton({ workspaceId, entry }: { workspaceId: string; entry: PlanColumn }) {
  const queryClient = useQueryClient();
  const unstart = useMutation({
    mutationFn: () => unstartSprint(workspaceId, entry.sprint.id),
    onSettled: () => invalidatePlanning(queryClient, workspaceId),
  });
  return (
    <button
      type="button"
      onClick={() => unstart.mutate()}
      disabled={unstart.isPending}
      className="h-8 rounded-md border border-line bg-base px-3 fs-12 font-semibold text-muted hover:text-ink disabled:opacity-50"
    >
      {unstart.isPending ? 'Stopping…' : 'Un-start'}
    </button>
  );
}

const MARK: Readonly<Record<Mark, { label: string; className: string } | null>> = {
  backlog: null,
  agreed: null,
  added: { label: 'Added by change of plan', className: 'text-violet-strong' },
  removed: { label: 'Taken out by change of plan', className: 'text-muted' },
  committed: { label: 'Committed', className: 'text-ink' },
  placed: { label: 'Placed', className: 'text-accent' },
  projected: { label: 'From ranking', className: 'text-faint' },
  delivered: null,
  carried: { label: 'Carried over', className: 'text-warn' },
};

function ItemList({
  label,
  aside,
  items,
  empty,
}: {
  label: string;
  aside: string;
  items: readonly PlanCardModel[];
  empty: string;
}) {
  return (
    <div>
      <div className="mb-2 flex flex-wrap justify-between gap-3">
        <SectionLabel>{label}</SectionLabel>
        <SectionLabel>{aside}</SectionLabel>
      </div>
      <div className="overflow-hidden rounded-xl border border-line">
        {items.length === 0 ? (
          <p className="px-4 py-5 fs-13 text-muted">{empty}</p>
        ) : (
          items.map((item) => <ItemRow key={item.requestId} item={item} />)
        )}
      </div>
    </div>
  );
}

function ItemRow({ item }: { item: PlanCardModel }) {
  const mark = MARK[item.mark];
  const sizeThresholds = useSizeThresholds();
  const removed = item.mark === 'removed';
  const right =
    item.mark === 'delivered' ? (
      <span className="fs-12 font-semibold whitespace-nowrap text-success">
        {item.closedAt !== null ? `Released ${formatDate(item.closedAt)}` : 'Released'}
      </span>
    ) : mark !== null && (item.mark !== 'added' || item.status === 'received') ? (
      <span className={cx('fs-11 font-semibold whitespace-nowrap', mark.className)}>{mark.label}</span>
    ) : (
      <StatusText status={item.status} />
    );

  return (
    <Link
      to={`/requests/${item.requestId}`}
      className="grid grid-cols-[minmax(0,1fr)_auto_7.5rem] items-center gap-3 border-b border-surface-3 bg-base px-4 py-3 last:border-b-0 hover:bg-surface"
    >
      <div className="min-w-0">
        <div className={cx('truncate font-semibold', removed ? 'text-muted line-through' : 'text-ink')}>
          {item.title}
        </div>
        <div className="truncate fs-11 text-muted">
          #{item.number} · {item.projectName ?? 'No project'}
          {item.approxHours > 0
            ? ` · ${SIZE_LABELS[sizeFromHours(item.approxHours, sizeThresholds)]} ~${formatHours(item.approxHours)}`
            : ' · Needs analysis'}
          {item.mark === 'added' && item.status !== 'received' ? ' · added by change of plan' : ''}
        </div>
      </div>
      <Pill tone={TYPE_TONES[item.type]}>{TYPE_LABELS[item.type]}</Pill>
      <div className="text-right">{right}</div>
    </Link>
  );
}

/** v0.3 §6.4 rule 6: flag At risk, name what will likely carry over, and say why. */
function RiskBanner({ entry }: { entry: PlanColumn }) {
  const named = likelyToCarryOver(entry.items);
  const over = entry.sprint.hotfixHours - entry.sprint.reserveHours;
  return (
    <div className="mb-6 flex items-start gap-3 rounded-lg border border-warn-border bg-warn-tint px-4 py-3 fs-13">
      <span className="mt-0.5 flex-none rounded-full bg-[#fdebc8] px-2 py-0.5 fs-11 font-bold text-warn">
        At risk
      </span>
      <div className="min-w-0 text-ink-soft">
        {formatHours(entry.sprint.hotfixHours)} of hotfixes this sprint against{' '}
        {formatHours(entry.sprint.reserveHours)} reserved — {formatHours(over)} came out of planned work.{' '}
        {named.length > 0 ? (
          <>
            Likely to carry over:{' '}
            {named.map((item, index) => (
              <span key={item.requestId}>
                {index > 0 && ', '}
                <Link to={`/requests/${item.requestId}`} className="font-semibold text-ink hover:text-accent">
                  #{item.number} {item.title}
                </Link>
              </span>
            ))}
            .
          </>
        ) : (
          'Everything in the sprint has started, so the overrun lands on what is in progress.'
        )}
      </div>
    </div>
  );
}

/** "Hotfixes: urgent work runs beside the sprint and releases on its own" (v0.3 §6.5). */
function HotfixLane({ entry, past = false }: { entry: PlanColumn; past?: boolean }) {
  const { sprint } = entry;
  const over = sprint.hotfixHours > sprint.reserveHours;
  return (
    <div className="min-w-0">
      <div className="mb-2 flex flex-wrap justify-between gap-3">
        <SectionLabel>{past ? 'Hotfixes that sprint' : 'Hotfixes'}</SectionLabel>
        <SectionLabel className={over ? 'text-danger' : 'text-success'}>
          {formatHours(sprint.hotfixHours)} of {formatHours(sprint.reserveHours)} reserved
        </SectionLabel>
      </div>
      <div className="overflow-hidden rounded-xl border border-line bg-surface">
        {sprint.hotfixes.length === 0 ? (
          <p className="px-4 py-4 fs-13 text-muted">
            {past ? 'No hotfixes closed that sprint.' : 'No hotfixes running.'}
          </p>
        ) : (
          sprint.hotfixes.map((hotfix) => <HotfixRow key={hotfix.requestId} hotfix={hotfix} />)
        )}
      </div>
      <p className="mt-2.5 fs-12 leading-relaxed text-muted">
        Urgent work runs beside the sprint and releases on its own. It uses the reserved share of team
        capacity; going over the reserve puts sprint items at risk.
      </p>
    </div>
  );
}

function HotfixRow({ hotfix }: { hotfix: DeliveryHotfix }) {
  // v0.3 §6.5 rule 6: a hotfix does not change the type — billing still follows it.
  const covered = hotfix.type === 'bug' || hotfix.type === 'support';
  return (
    <Link
      to={`/requests/${hotfix.requestId}`}
      className="flex flex-col gap-1 border-b border-surface-3 px-4 py-3 last:border-b-0 hover:bg-base"
    >
      <div className="truncate font-semibold text-ink">{hotfix.title}</div>
      <div className="flex items-center gap-2 fs-11 text-muted">
        {hotfix.priority === 'urgent' && <span className="font-semibold text-danger">Urgent</span>}
        <span className="truncate">
          #{hotfix.number} · {TYPE_LABELS[hotfix.type]} ·{' '}
          {covered ? 'covered by retainer' : 'uses included hours'}
        </span>
        <span className="ml-auto">
          <StatusText status={hotfix.status} className="fs-11" />
        </span>
      </div>
    </Link>
  );
}

function CapacityAside({ entry }: { entry: PlanColumn }) {
  const { sprint } = entry;
  const committed = entry.items.filter((item) => item.mark === 'committed').length;
  const ranking = entry.items.filter((item) => item.mark === 'projected').length;
  return (
    <div className="min-w-0">
      <SectionLabel className="mb-2">Capacity</SectionLabel>
      <div className="flex flex-col gap-3 rounded-xl border border-line px-4 py-4">
        <p className="fs-13 leading-relaxed text-ink-soft">
          {entry.state === 'planning'
            ? `${committed} committed by hand, ${ranking} pulled in by the ranking. Anything over capacity gets cut at planning unless something else comes out.`
            : 'Filled from the backlog ranking against capacity. Place work in it from Plan to fix it in place.'}
        </p>
        <Meter width={entry.width} band={entry.band} thick />
        <div className="flex justify-between fs-11 text-muted">
          <span className="whitespace-nowrap">
            {formatHours(entry.hours)} of {formatHours(sprint.plannableHours)}
          </span>
          <span
            className={cx(
              'font-semibold whitespace-nowrap',
              entry.band === null ? '' : LOAD_TEXT[entry.band],
            )}
          >
            {entry.loadLabel}
          </span>
        </div>
      </div>
    </div>
  );
}

/**
 * Close the sprint.
 *
 * The server refuses a release while anything committed is unfinished (see
 * SprintsService.update), so this modal is where that gets resolved rather
 * than a confirmation of something already decided. It used to be the latter:
 * it listed the unfinished items and said, in a sentence, that they would
 * carry over — and then Release did both things at once. Closing a week and
 * writing off six commitments were one click, and the second one left no
 * decision behind it, which is precisely the "scope changed and nobody
 * recorded why" this product exists to stop.
 *
 * Now each unfinished item is ticked off deliberately. Ticked means "this did
 * not get done, carry it over"; left unticked means "I am about to finish it",
 * and the release stays disabled until none are left in that state. Carrying
 * over is its own request, so it lands in the trail as its own decision even
 * if the release that follows fails.
 */
function ReleaseButton({ workspaceId, entry }: { workspaceId: string; entry: PlanColumn }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [carry, setCarry] = useState<ReadonlySet<string>>(new Set());

  const unfinished = entry.items.filter((item) => item.removedAt === null && item.status !== 'released');
  const allTicked = unfinished.every((item) => carry.has(item.requestId));

  const release = useMutation({
    mutationFn: async () => {
      // Carry over first, then release: the server refuses the release while
      // anything is unfinished, so the order is not a preference.
      const toCarry = unfinished.filter((item) => carry.has(item.requestId)).map((item) => item.requestId);
      if (toCarry.length > 0) await carryOverItems(workspaceId, entry.sprint.id, toCarry);
      return releaseSprint(workspaceId, entry.sprint.id);
    },
    onSuccess: async () => {
      await invalidatePlanning(queryClient, workspaceId);
      setCarry(new Set());
      setOpen(false);
    },
  });

  const toggle = (requestId: string) =>
    setCarry((current) => {
      const next = new Set(current);
      if (next.has(requestId)) next.delete(requestId);
      else next.add(requestId);
      return next;
    });

  const carryCount = unfinished.filter((item) => carry.has(item.requestId)).length;

  return (
    <>
      <Button size="sm" onClick={() => setOpen(true)}>
        Release sprint
      </Button>
      <Modal
        open={open}
        onOpenChange={setOpen}
        title={`Release ${entry.sprint.name}?`}
        description={`Closes the sprint that ran ${sprintDate(entry.sprint.startsOn)} – ${sprintDate(entry.sprint.endsOn)}.`}
        footer={
          <>
            <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              variant="primary"
              disabled={release.isPending || !allTicked}
              onClick={() => release.mutate()}
            >
              {release.isPending
                ? 'Releasing…'
                : carryCount > 0
                  ? `Carry over ${carryCount} and release`
                  : 'Release sprint'}
            </Button>
          </>
        }
      >
        {unfinished.length === 0 ? (
          <p className="fs-13 text-ink-soft">Everything in this sprint has been released.</p>
        ) : (
          <div className="flex flex-col gap-2.5 fs-13">
            <p className="text-ink-soft">
              {unfinished.length} {unfinished.length === 1 ? 'item is' : 'items are'} not finished. Move
              {unfinished.length === 1 ? ' it' : ' them'} to Done on the Board, or tick
              {unfinished.length === 1 ? ' it' : ' them'} here to carry
              {unfinished.length === 1 ? ' it' : ' them'} over to the top of the backlog. The sprint&rsquo;s
              frozen record keeps {unfinished.length === 1 ? 'it' : 'them'} as agreed either way.
            </p>
            <ul className="flex flex-col gap-1">
              {unfinished.map((item) => (
                <li key={item.requestId}>
                  <label className="flex items-start gap-2 rounded-lg border border-line px-2.5 py-2 hover:bg-surface-2">
                    <input
                      type="checkbox"
                      className="mt-[0.1875rem] flex-none"
                      checked={carry.has(item.requestId)}
                      onChange={() => toggle(item.requestId)}
                    />
                    <span className="min-w-0 flex-1">
                      <Link
                        to={`/requests/${item.requestId}`}
                        className="fs-13 font-semibold text-ink hover:text-accent hover:underline"
                      >
                        #{item.number} {item.title}
                      </Link>
                      <span className="block fs-11 text-muted">
                        <StatusText status={item.status} /> · carry over to the backlog
                      </span>
                    </span>
                  </label>
                </li>
              ))}
            </ul>
            {!allTicked && (
              <p className="fs-12 text-muted">
                Tick every unfinished item, or finish the rest on the Board first — a sprint does not close
                with work still pointed at it.
              </p>
            )}
          </div>
        )}
        {release.error !== null && (
          <p role="alert" className="mt-2 fs-12 text-danger">
            {release.error.message}
          </p>
        )}
      </Modal>
    </>
  );
}
